/**
 * The persistent "Messaging" dock (docs/modules/messaging.md "Three presentation modes"): mounted
 * once by the shell inside `DockProvider`, it docks a collapsible panel through `useDock()`.
 * Facebook-style: Exxy (the help assistant) is a pinned chat at the top, and real conversations
 * follow when the member can message (org member on a portal page). It also hosts the pop-out
 * windows (so they share this window's SSE stream) and the cross-window sync.
 *
 * The panel derives "can message" itself (rather than taking it as a prop) so it is registered in
 * the dock once per sign-in and keeps its state (open conversation, Exxy transcript) across
 * navigation, instead of remounting whenever the user moves between portal and admin pages.
 */
import { useDock } from '@oex/ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { Bell, BellOff, ChevronDown, ChevronLeft, ChevronUp, MessageCircle, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ExxyChat } from '../../features/assistant';
import { useUiSettings } from '../ui-settings';
import {
  PopoutHost,
  PopoutThread,
  ThreadList,
  ThreadView,
  UnreadBadge,
  useSyncInvalidation,
  useThreads,
  useUnread,
} from '../../features/messaging';
import { useCan } from '../can';
import { useT } from '../i18n';
import { useSession } from '../session';

export const DOCK_ID = 'messaging';
const RECENT = { archived: 'false' as const, limit: 5 };
const EXXY = 'exxy';

/** Real messaging (threads) is available to an org member with message:read on a portal page. */
function useCanMessage(): boolean {
  const { status, activeOrgId } = useSession();
  const canRead = useCan('message:read');
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onPortal = pathname.startsWith('/portal') && !pathname.startsWith('/portal/messages/popout');
  return status === 'ready' && Boolean(activeOrgId) && canRead.allowed && onPortal;
}

export function MessagingDockPanel() {
  const t = useT();
  const canMessage = useCanMessage();
  const { settings, save } = useUiSettings();
  const soundOn = settings.messagingSound ?? false;
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const threads = useThreads(RECENT, canMessage);
  const unread = useUnread(canMessage);
  const total = unread.data?.total ?? 0;

  // One conversation at a time, inside the same box (request #1: a single box, no second floating
  // card that overlaps the page). Opening a row implies the dock is expanded.
  const show = (id: string) => {
    setExpanded(true);
    setOpen(id);
  };
  const conversation = expanded && open ? open : null;

  return (
    <div className="flex flex-col items-end" data-testid="messaging-dock">
      <div className="matrix-card flex w-96 max-w-[calc(100vw-2rem)] flex-col p-0">
        {conversation ? (
          // Conversation header with a back arrow to the list. Threads carry their own header
          // (title, mute, pop-out, close) inside ThreadView, so only Exxy needs one here.
          conversation === EXXY ? (
            <div className="flex items-center gap-2 border-b border-border px-2 py-2">
              <button
                type="button"
                aria-label={t('messaging.dock.back')}
                onClick={() => setOpen(null)}
                className="rounded p-1 text-muted-foreground signal-focus hover:text-foreground"
              >
                <ChevronLeft aria-hidden className="size-4" />
              </button>
              <span className="flex items-center gap-2 truncate font-display text-sm font-semibold">
                <Sparkles aria-hidden className="size-4 text-[var(--oex-cyan)]" />
                {t('assistant.name')}
              </span>
            </div>
          ) : null
        ) : (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={t('messaging.dock.toggle')}
            onClick={() => setExpanded((e) => !e)}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm font-semibold signal-focus"
          >
            <MessageCircle className="size-4" aria-hidden />
            <span className="flex-1 text-left">{t('messaging.dock.title')}</span>
            <UnreadBadge count={total} label={t('messaging.dock.unread', { count: total })} />
            {expanded ? (
              <ChevronDown className="size-4" aria-hidden />
            ) : (
              <ChevronUp className="size-4" aria-hidden />
            )}
          </button>
        )}

        {conversation ? (
          <div
            className="flex h-[28rem] max-h-[calc(100dvh-10rem)] min-h-0 flex-col"
            role="dialog"
            aria-label={conversation === EXXY ? t('assistant.name') : t('messaging.dock.title')}
          >
            {conversation === EXXY ? (
              <ExxyChat />
            ) : (
              <div className="min-h-0 flex-1 p-3">
                <ThreadView threadId={conversation} onClose={() => setOpen(null)} />
              </div>
            )}
          </div>
        ) : expanded ? (
          <div className="border-t border-border">
            <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground">
              <span>{t('messaging.sound.label')}</span>
              <button
                type="button"
                aria-label={soundOn ? t('messaging.sound.off') : t('messaging.sound.on')}
                aria-pressed={soundOn}
                onClick={() => void save({ messagingSound: !soundOn })}
                className="rounded p-1 signal-focus hover:text-foreground"
              >
                {soundOn ? <Bell className="size-4" aria-hidden /> : <BellOff className="size-4" aria-hidden />}
              </button>
            </div>
            <div className="max-h-[28rem] overflow-auto">
              {/* Exxy, pinned at the top like a Messenger contact. */}
              <button
                type="button"
                data-testid="exxy-row"
                onClick={() => show(EXXY)}
                className="flex w-full items-start gap-2 border-b border-border px-2 py-2 text-left hover:bg-accent signal-focus"
              >
                <Sparkles className="mt-0.5 size-4 text-[var(--oex-cyan)]" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{t('assistant.name')}</span>
                  <span className="block truncate text-xs text-muted-foreground">{t('assistant.tagline')}</span>
                </span>
              </button>
              {canMessage ? (
                <>
                  <ThreadList threads={threads.data?.items ?? []} selected={null} onSelect={show} />
                  <Link to="/portal/messages" className="block px-3 py-2 text-xs underline">
                    {t('messaging.dock.all')}
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function MessagingDock() {
  const { status } = useSession();
  const signedIn = status === 'ready';
  const canMessage = useCanMessage();
  const { open, close } = useDock();
  useEffect(() => {
    if (signedIn) open(DOCK_ID, <MessagingDockPanel />);
    else close(DOCK_ID);
  }, [signedIn, open, close]);
  useEffect(() => () => close(DOCK_ID), [close]);
  // Pop-outs and cross-window sync only matter for real threads.
  return canMessage ? <DockServices /> : null;
}

/** Pop-out host and cross-window sync; mounted whenever real messaging is available. */
function DockServices() {
  useSyncInvalidation();
  return (
    <PopoutHost render={(threadId, onClose) => <PopoutThread threadId={threadId} onClose={onClose} />} />
  );
}
